/**
 * The desktop Explorer (docs/ux/design-language.md §7c; app-clarity "IDE sidebar"): a tree that
 * replaces the project tab strip on desktop. Home, then each pinned project (its counts in fixed
 * columns: blocked · needs you · running), then its lanes, then each lane's live runs. Pure: the
 * sidebar renders the flat list of visible rows this returns.
 */
import type { StatusKind } from '@viko/transcript-ui'
import type { RunSummary, WorkstreamDetail } from './types.js'

export type ExplorerKind = 'home' | 'project' | 'lane' | 'run'

export interface ExplorerCounts {
  blocked: number
  needs: number
  running: number
}

export interface ExplorerRow {
  id: string
  kind: ExplorerKind
  depth: number
  label: string
  /** Parent row id (for ← to the parent). */
  parent?: string
  expandable: boolean
  open: boolean
  /** Status icon for lanes and runs. */
  status?: StatusKind
  counts?: ExplorerCounts
  /** One quiet word on the right of a child row ("landed", "3 queued"). */
  meta?: string
  projectId?: string
  runId?: string
  /** ⌘/⌥ number position (projects 1–9; Home 0). */
  index?: number
}

export interface ExplorerProject {
  id: string
  name: string
  running: number
}

export interface ExplorerInput {
  projects: readonly ExplorerProject[]
  totals: ExplorerCounts
  counts: (projectId: string) => { blocked: number; needs: number }
  /** Needs-you per lane (workstream id). */
  laneNeeds: ReadonlyMap<string, number>
  lanes: readonly ExplorerLane[]
  runs: readonly RunSummary[]
  open: ReadonlySet<string>
  filter?: string
}

/** A lane as the Explorer shows it. */
export interface ExplorerLane {
  id: string
  projectId: string
  name: string
  status: StatusKind
  queued: number
  landed: boolean
  rank: number
}

/** `GET /api/workstreams` rows → lanes (archived ones leave the tree). */
export function lanesOf(ws: readonly WorkstreamDetail[]): ExplorerLane[] {
  return ws
    .filter((w) => w.status !== 'archived')
    .map((w) => ({
      id: w.id,
      projectId: w.projectId,
      name: w.name,
      status:
        w.liveRun?.status === 'waiting'
          ? ('wait' as const)
          : w.liveRun
            ? ('run' as const)
            : w.status === 'blocked'
              ? ('blocked' as const)
              : w.status === 'landed' || w.status === 'done'
                ? ('ok' as const)
                : ('idle' as const),
      queued: w.counts?.open ?? 0,
      landed: w.status === 'landed' || w.status === 'done',
      rank: w.rank ?? w.createdAt,
    }))
    .sort((a, b) => a.rank - b.rank)
}

const liveRun = (r: RunSummary) =>
  r.status === 'running' || r.status === 'waiting' || r.status === 'compacting'

export const projectNodeId = (id: string) => `p:${id}`
export const laneNodeId = (id: string) => `w:${id}`

/** The visible rows, in order. A filter keeps matches and their ancestors (opened). */
export function explorerRows(input: ExplorerInput): ExplorerRow[] {
  const q = input.filter?.trim().toLowerCase() ?? ''
  const hit = (s: string) => !q || s.toLowerCase().includes(q)
  const out: ExplorerRow[] = []
  if (!q)
    out.push({
      id: 'home',
      kind: 'home',
      depth: 0,
      label: 'Home',
      expandable: false,
      open: false,
      counts: input.totals,
      index: 0,
    })
  input.projects.forEach((p, i) => {
    const pid = projectNodeId(p.id)
    const lanes = input.lanes.filter((w) => w.projectId === p.id)
    const laneRows: ExplorerRow[] = []
    for (const w of lanes) {
      const wid = laneNodeId(w.id)
      const runs = input.runs.filter((r) => r.workstreamId === w.id && liveRun(r))
      const needs = input.laneNeeds.get(w.id) ?? 0
      const queued = w.queued
      const runRows: ExplorerRow[] = runs
        .filter((r) => hit(r.title ?? r.runId) || hit(w.name) || hit(p.name))
        .map((r) => ({
          id: `r:${r.runId}`,
          kind: 'run' as const,
          depth: 2,
          label: r.title ?? 'Untitled run',
          parent: wid,
          expandable: false,
          open: false,
          status: r.status === 'waiting' ? ('wait' as const) : ('run' as const),
          projectId: p.id,
          runId: r.runId,
        }))
      const laneHit = hit(w.name) || hit(p.name)
      if (q && !laneHit && !runRows.length) continue
      const open = runs.length > 0 && (input.open.has(wid) || (!!q && runRows.length > 0))
      laneRows.push({
        id: wid,
        kind: 'lane',
        depth: 1,
        label: w.name,
        parent: pid,
        expandable: runs.length > 0,
        open,
        status: needs ? 'wait' : w.status,
        ...(needs
          ? { counts: { blocked: 0, needs, running: 0 } }
          : w.landed
            ? { meta: 'landed' }
            : queued && w.status !== 'run'
              ? { meta: `${queued} queued` }
              : {}),
        projectId: p.id,
      })
      if (open) laneRows.push(...runRows)
    }
    if (q && !hit(p.name) && !laneRows.length) return
    const open = lanes.length > 0 && (input.open.has(pid) || (!!q && laneRows.length > 0))
    const c = input.counts(p.id)
    out.push({
      id: pid,
      kind: 'project',
      depth: 0,
      label: p.name,
      expandable: lanes.length > 0,
      open,
      counts: { blocked: c.blocked, needs: c.needs, running: p.running },
      projectId: p.id,
      index: i + 1,
    })
    if (open) out.push(...laneRows)
  })
  return out
}

const OPEN_KEY = 'viko.explorer.open'
const WIDTH_KEY = 'viko.explorer.width'
const HIDDEN_KEY = 'viko.explorer.hidden'
export const SIDEBAR_MIN = 220
export const SIDEBAR_MAX = 420
export const SIDEBAR_DEFAULT = 288

export function loadOpen(): Set<string> {
  try {
    const v = JSON.parse(localStorage.getItem(OPEN_KEY) ?? '[]')
    return new Set(Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [])
  } catch {
    return new Set()
  }
}
export function saveOpen(open: ReadonlySet<string>) {
  try {
    localStorage.setItem(OPEN_KEY, JSON.stringify([...open].slice(0, 200)))
  } catch {
    // private window: this page load only
  }
}
export const clampWidth = (w: number) => Math.round(Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, w)))
export function loadWidth(): number {
  try {
    const n = Number(localStorage.getItem(WIDTH_KEY))
    return n ? clampWidth(n) : SIDEBAR_DEFAULT
  } catch {
    return SIDEBAR_DEFAULT
  }
}
export function saveWidth(w: number) {
  try {
    localStorage.setItem(WIDTH_KEY, String(clampWidth(w)))
  } catch {
    // ignore
  }
}
export function loadHidden(): boolean {
  try {
    return localStorage.getItem(HIDDEN_KEY) === '1'
  } catch {
    return false
  }
}
export function saveHidden(h: boolean) {
  try {
    localStorage.setItem(HIDDEN_KEY, h ? '1' : '0')
  } catch {
    // ignore
  }
}
